import { MapPin, Video, MoreVertical } from "lucide-react";
import { Badge } from "../../@/components/ui/badge";
import { Button } from "../../@/components/ui/button";
import { formatInTimeZone } from "date-fns-tz";
import {
  DropdownMenuContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "../../@/components/ui/dropdown-menu";
import DeleteClassSession from "../forms/deleteClassSession";
import { getUserTimezone } from "../lib/timezone";

// Utility to format date and time with timezone
const formatDate = (date, tz) => formatInTimeZone(date, tz, "EEE, d MMM");
const formatTime = (date, tz) =>
  formatInTimeZone(date, tz, "h:mmaaa").replace(/AM|PM/, (m) =>
    m.toLowerCase(),
  );

// Get timezone offset string (e.g., "GMT+3", "GMT-5")
const getTimezoneOffset = (tz) => {
  try {
    const date = new Date();
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    });
    const parts = fmt.formatToParts(date);
    const offsetPart = parts.find((p) => p.type === "timeZoneName");
    return offsetPart?.value || "";
  } catch {
    return "";
  }
};

// Get short timezone name for display (e.g., "Africa/Nairobi" -> "Nairobi")
const getTimezoneDisplayName = (tz) => {
  if (!tz) return "";
  const parts = tz.split("/");
  return parts[parts.length - 1].replace(/_/g, " ");
};

// Use user's preferred timezone as default
const DEFAULT_TIMEZONE = getUserTimezone();

// Ensure date string is interpreted as UTC
const parseAsUtc = (dateInput) => {
  if (!dateInput) return null;
  if (dateInput instanceof Date) return dateInput;

  let dateStr = String(dateInput);
  // If the date string doesn't have a timezone indicator, treat it as UTC
  if (
    !dateStr.endsWith("Z") &&
    !dateStr.includes("+") &&
    !dateStr.includes("-", 10)
  ) {
    dateStr = dateStr + "Z";
  }
  return new Date(dateStr);
};

const ClassCard = ({
  startTime,
  endTime,
  title = "Untitled Class",
  instructor = "TBA",
  location = "Online",
  timeZone = DEFAULT_TIMEZONE,
  onEdit,
  onDelete,
  meetingLink,
  sessionType = "class",
  room,
  sessionId,
  classId,
  tmId,
  recurrencePattern,
  refetchClasses,
  canManageClass = false,
}) => {
  // Convert to Date objects, ensuring UTC interpretation
  const start = parseAsUtc(startTime);
  const end = parseAsUtc(endTime);

  // Compute date string, time strings
  const dateStr = formatDate(start, timeZone);
  const startStr = formatTime(start, timeZone);
  const endStr = formatTime(end, timeZone);

  // Get timezone display info
  const tzDisplayName = getTimezoneDisplayName(timeZone);
  const tzOffset = getTimezoneOffset(timeZone);
  const timezoneLabel =
    tzDisplayName && tzOffset
      ? `(${tzDisplayName} ${tzOffset})`
      : tzOffset
        ? `(${tzOffset})`
        : "";

  // Compute if class is live
  const now = new Date();
  const isLive = now >= start && now <= end;
  // const isLive = true

  const typeLabels = {
    class: "Class",
    event: "Event",
    meeting: "Meeting",
  };

  return (
    <div className="w-full mx-auto border border-(--neutral-300) rounded-lg p-4 md:p-6 shadow-sm text-(--neutral-900)">
      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
        {/* Header with date and time */}
        <div className="shrink-0 md:min-w-0 text-(--neutral-700)">
          <div className="flex flex-col gap-1">
            <h4 className="text-base md:text-lg font-semibold text-(--neutral-900) font-heading">
              {dateStr}
            </h4>
            <p className="text-(--neutral-800) font-body -mt-2 flex flex-col gap-0.2">
              {startStr} - {endStr}{" "}
              <span className="text-xs text-(--neutral-500)">
                {timezoneLabel}
              </span>
            </p>
            <p className="  text-(--neutral-800)">
              By <span className="font-semibold">{instructor}</span>
            </p>
          </div>
        </div>
        {/* Vertical divider - hidden on mobile */}
        <div className="hidden md:block border border-(--neutral-300) self-stretch"></div>
        {/* Horizontal divider - visible on mobile */}
        <div className="block md:hidden border-t border-(--neutral-300) w-full"></div>
        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-start gap-0.5">
            <h3 className="font-heading font-semibold text-(--neutral-900) leading-tight">
              {title}
            </h3>
            {isLive && (
              <Badge
                variant={"completed"}
                className="bg-(--color-success) uppercase text-white mt-1"
              >
                Ongoing
              </Badge>
            )}
          </div>
          {/* Class label and location */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3 mt-1">
            <Badge className="uppercase">
              {typeLabels[sessionType] || sessionType}
            </Badge>
            <div className="flex items-start sm:items-center md:text-base text-(--neutral-800)">
              <MapPin
                size={20}
                className="shrink-0 mr-1.5 sm:mt-0 text-(--neutral-800)"
              />
              <span className="leading-relaxed">{location}</span>
            </div>
          </div>
          {/* Join Live Class button */}
          <div className="flex items-center gap-3">
            {meetingLink && (
              <a
                href={meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button variant="outline">
                  Join Live Class
                  <Video size={30} />
                </Button>
              </a>
            )}

            {canManageClass ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="">
                    <MoreVertical size={22} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {typeof onEdit === "function" && (
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        onEdit();
                      }}
                    >
                      Edit
                    </DropdownMenuItem>
                  )}
                  <DeleteClassSession
                    sessionId={sessionId}
                    classId={classId}
                    tmId={tmId}
                    recurrencePattern={recurrencePattern}
                    sessionTitle={title}
                    triggerButtonProps={{
                      className:
                        "w-full text-left px-2 py-1 bg-transparent border-none text-(--color-primary) hover:bg-(--neutral-50)",
                      style: { justifyContent: "flex-start" },
                    }}
                    onDeleted={() => {
                      if (typeof refetchClasses === "function")
                        refetchClasses();
                      if (typeof onDelete === "function") onDelete();
                    }}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassCard;
